import { useState } from 'react';

import { useCoverLines } from '../hooks/use-cover-lines';

interface Props {
  /** `undefined` = random; a string = a curated or custom cover line. */
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}

const RANDOM = '__random__';
const CUSTOM = '__custom__';

/**
 * Cover-line selector for the composer. A dropdown of curated lines plus a
 * "Random" default and a "Custom…" option that reveals a free-text field.
 * Automated runs always render with a random line (no cover line sent).
 */
export function CoverLinePicker({ value, onChange }: Props) {
  const { data, isLoading, error } = useCoverLines();
  const coverLines = data?.coverLines ?? [];

  const isPreset = value !== undefined && coverLines.includes(value);
  const [customMode, setCustomMode] = useState(
    value !== undefined && !isPreset,
  );

  const selectValue = customMode
    ? CUSTOM
    : value === undefined
      ? RANDOM
      : value;

  function onSelectChange(next: string) {
    if (next === RANDOM) {
      setCustomMode(false);
      onChange(undefined);
    } else if (next === CUSTOM) {
      setCustomMode(true);
      onChange('');
    } else {
      setCustomMode(false);
      onChange(next);
    }
  }

  if (error) {
    return (
      <div className="error">Could not load cover lines: {error.message}</div>
    );
  }

  return (
    <>
      <select
        className="select"
        value={selectValue}
        disabled={isLoading}
        onChange={(e) => onSelectChange(e.target.value)}
      >
        <option value={RANDOM}>Random — pick one automatically</option>
        {coverLines.map((line) => (
          <option key={line} value={line}>
            {line}
          </option>
        ))}
        <option value={CUSTOM}>Custom…</option>
      </select>
      {customMode ? (
        <input
          type="text"
          placeholder="Custom cover line — {count} = story count"
          maxLength={200}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : null}
    </>
  );
}
